import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormModal, Input, LoadingSpinner, useToastActions } from '@airclou/ui';
import { Search, Check } from 'lucide-react';
import { listAvailableUsers, addGroupMembers } from '@/api/groups';
import type { AvailableUser } from '@/api/groups';

interface AddMembersToGroupModalProps {
  companyId: string;
  groupId: string;
  groupName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddMembersToGroupModal({
  companyId,
  groupId,
  groupName,
  isOpen,
  onClose,
  onSuccess,
}: AddMembersToGroupModalProps) {
  const { t } = useTranslation('dashboard');
  const { success, error: showError } = useToastActions();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<AvailableUser[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listAvailableUsers(companyId, groupId, searchQuery || undefined);
      setUsers(res.users);
    } catch {
      // Silently handle - users will see empty list
    } finally {
      setLoading(false);
    }
  }, [companyId, groupId, searchQuery]);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen, loadUsers]);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
      setSearchQuery('');
    }
  }, [isOpen]);

  const toggleUser = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedIds.size === 0) return;

    setSubmitting(true);
    try {
      await addGroupMembers(companyId, groupId, Array.from(selectedIds));
      success(t('groups.membersAdded', { count: selectedIds.size, label: selectedIds.size === 1 ? 'member' : 'members' }));
      onSuccess();
    } catch (err: any) {
      const code = err.code;
      if (code === 'USER_NOT_IN_COMPANY') {
        showError(t('groups.userNotInCompany'));
      } else {
        showError(err.message || t('groups.failedToAddMembers'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('groups.addMembersTo', { name: groupName })}
      size="md"
      submitText={submitting ? t('common.adding') : `${t('common.add')} ${selectedIds.size} ${selectedIds.size === 1 ? t('common.member') : t('common.members')}`}
      canSubmit={selectedIds.size > 0 && !submitting}
      isLoading={submitting}
      onSubmit={handleSubmit}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('groups.selectUsers')}
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder={t('groups.searchUsers')}
              value={searchQuery}
              onChange={(val) => setSearchQuery(val)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner text="Loading users..." textPosition="right" />
            </div>
          ) : users.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {searchQuery ? t('groups.noMatchingUsers') : t('groups.allUsersInGroup')}
            </div>
          ) : (
            users.map((user) => {
              const isSelected = selectedIds.has(user.user_id);
              return (
                <button
                  key={user.user_id}
                  type="button"
                  onClick={() => toggleUser(user.user_id)}
                  className={`flex items-center gap-3 w-full px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                    isSelected ? 'bg-primary/5 dark:bg-primary/10' : ''
                  }`}
                >
                  <div
                    className={`flex items-center justify-center w-5 h-5 rounded border flex-shrink-0 ${
                      isSelected
                        ? 'bg-primary border-primary'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/15 dark:bg-primary/25 text-primary dark:text-[hsl(var(--primary-hover))] text-sm font-medium flex-shrink-0">
                      {user.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-foreground truncate text-sm">
                        {user.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {user.email}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {selectedIds.size > 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('groups.usersSelected', { count: selectedIds.size, label: selectedIds.size === 1 ? 'user' : 'users' })}
          </p>
        )}
      </div>
    </FormModal>
  );
}
